import { useMemo, useState } from 'react';
import { useAppDispatch } from '../../../hooks/useRedux';
import classNames from 'classnames/bind';
import styles from './SignIn.module.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useLoginMutation } from '../../../services/api';
import { setCredentials } from '../../../features/authSlice';
import { menuChanged } from '../../../features/infoSlice';

import { loginResponse } from '../../../types/login';

import {
  ENTER,
  ENTER_EMAIL,
  ENTER_EMAIL_MESSAGE,
  ENTER_PASSWORD,
  ENTER_PASSWORD_MESSAGE,
  FORGOT_PASSWORD,
  GUEST_VIEW,
  ID,
  ID_SAVE,
  LINK_PWD,
  LOGIN,
  NOT_YET_MEMBER,
  OR,
  PASSWORD,
  SIGNUP,
} from '../../../constants';

import { setLocalStorage, setToken } from '../../../utils/storage';
import { deleteCookie, getCookie, setCookie } from '../../../utils/cookie';
import NaverLogin from './NaverLogin/NaverLogin';

import { openSelpubCloud } from '../../../utils/selpub';
import useModal from '../../../hooks/useModal';
import LanguageSelector from '../../../components/LanguageSelector';

export default function SignIn() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { state } = useLocation();
  const signInSchema = yup.object({
    email: yup.string().required(),
    pwd: yup.string().required(),
  });
  type FormData = yup.InferType<typeof signInSchema>;

  const user = useMemo(() => getCookie('user'), []);

  const { register, handleSubmit, getValues, watch } = useForm({
    resolver: yupResolver(signInSchema),
    defaultValues: user
      ? { email: user, pwd: '' }
      : { email: 'guest@selvas.com', pwd: 'guest1234' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const email = watch('email');
  const password = watch('pwd');
  const isSubmitEnabled = email.trim() !== '' && password.trim() !== '';

  const [isRememberId, setIsRememberId] = useState(!!user);
  const [loginRes, setLoginRes] = useState<loginResponse>();

  const [login] = useLoginMutation();
  const navigate = useNavigate();
  const { toggleModal } = useModal();

  const openViewerByLink = (userType?: 'guest' | 'user') => {
    if (state.hasLinkPwd) {
      toggleModal({
        name: LINK_PWD,
        id: 'linkPassword',
        type: 'alert',
        isOpen: true,
        initialButtonDisabled: true,
        data: { link: state.link, projectId: state.projectId },
      });
    } else {
      openSelpubCloud(
        'viewer',
        state.projectId,
        userType === 'guest',
        undefined,
        userType === 'guest'
      );
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      const res = await login(data).unwrap();
      const loginResData = res.data;

      setLoginRes(res);

      if (loginResData.accessToken) {
        const email = getValues('email') as string;
        setToken(loginResData);
        dispatch(
          setCredentials({
            accessToken: loginResData.accessToken,
            refreshToken: loginResData.refreshToken,
          })
        );
        setLocalStorage('loggedIn', 1);
        navigate('/home', {
          replace: true,
        });
        isRememberId ? setCookie('user', email) : deleteCookie('user');
        dispatch(menuChanged('main'));

        if (state?.link) {
          openViewerByLink();
        }
      } else if (loginResData.status === 1) {
        navigate('inactive-account', { state: { accountInfo: loginResData } });
      } else if (loginResData.status === 2) {
        navigate('disabled-account', { state: { accountInfo: loginResData } });
      }
    } catch (error) {
      console.log('Failed to login', error);
    }
  };

  const rememberId = (e: React.ChangeEvent<HTMLInputElement>) =>
    setIsRememberId(e.target.checked);

  return (
    <>
      <h2 className={cx('blind')}>{LOGIN}</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={cx('input_area')}>
          <div className={cx('input_box')}>
            <span className={cx('blind')}>{`${ID} ${ENTER}`}</span>
            <input
              type="email"
              title={ENTER_EMAIL}
              placeholder={ENTER_EMAIL_MESSAGE}
              autoComplete="username"
              autoCapitalize="none"
              {...register('email')}
            />
          </div>

          <div
            className={cx('input_box', {
              invalid: loginRes?.message && loginRes?.message !== 'OK',
            })}
          >
            <span className={cx('blind')}>
              `${PASSWORD} ${ENTER}`
            </span>
            <input
              type="password"
              title={ENTER_PASSWORD}
              placeholder={ENTER_PASSWORD_MESSAGE}
              autoComplete="current-password"
              autoCapitalize="none"
              {...register('pwd')}
            />
            {loginRes?.message !== 'OK' && (
              <span className={cx('msg', 'invalid')}>{loginRes?.message}</span>
            )}
          </div>

          <div className={cx('remember_box')}>
            <label>
              <input
                type="checkbox"
                className={cx('blind')}
                onChange={rememberId}
                checked={isRememberId}
              />
              <span className={cx('clabel')}>{`${ID_SAVE}`}</span>
            </label>
            <Link to="forgot-password">
              <span>{`${FORGOT_PASSWORD}`}</span>
            </Link>
          </div>
        </div>

        <div className={cx('btn_wrap', 'bottom')}>
          <button
            type="submit"
            className={cx('btn', 'b_t', 's48', {
              'is--disabled': !isSubmitEnabled,
            })}
          >
            <span>{LOGIN}</span>
          </button>

          {/* <span className={cx('dividing')}>
            <span>{OR}</span>
          </span>

          <NaverLogin />

          <p
            className={cx('explain_txt', { mb10: state?.userType !== 'guest' })}
          >
            {NOT_YET_MEMBER}
            <Link to="signup" className={cx('link_t')}>
              {' ' + SIGNUP}
            </Link>
          </p> */}

          {state?.userType === 'guest' && (
            <button
              type="button"
              className={cx('btn', 'b_t', 's48')}
              onClick={() => openViewerByLink('guest')}
            >
              <span>{GUEST_VIEW}</span>
            </button>
          )}
        </div>
      </form>
      {/* <LanguageSelector /> */}
    </>
  );
}
